class RtaHomeHero extends HTMLElement {
  connectedCallback() {
    this.lifecycleController?.abort();
    this.revealController?.abort();
    window.clearTimeout(this.revealTimer);
    window.clearTimeout(this.resetTimer);
    window.clearTimeout(this.mobileRevealTimer);
    this.mobileRevealTimer = null;

    this.lifecycleController = new AbortController();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.forcedColors = window.matchMedia('(forced-colors: active)');
    this.mobileIntro = window.matchMedia('(max-width: 1199.98px)');
    this.desktopPointer = window.matchMedia('(min-width: 1200px) and (hover: hover) and (pointer: fine)');
    this.reveal = this.reveal.bind(this);
    this.revealImmediately = this.revealImmediately.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleMotionPreferenceChange = this.handleMotionPreferenceChange.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
    this.handleSectionLoad = this.handleSectionLoad.bind(this);
    this.handleSectionUnload = this.handleSectionUnload.bind(this);
    this.handleHomeControlClick = this.handleHomeControlClick.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);

    this.mountSelection();

    const lifecycleOptions = { signal: this.lifecycleController.signal };
    document.addEventListener('shopify:block:select', this.handleBlockSelect, lifecycleOptions);
    document.addEventListener('shopify:block:deselect', this.handleBlockDeselect, lifecycleOptions);
    document.addEventListener('shopify:section:load', this.handleSectionLoad, lifecycleOptions);
    document.addEventListener('shopify:section:unload', this.handleSectionUnload, lifecycleOptions);
    // A restored page stays settled and keeps its selected image and scroll position.
    window.addEventListener('pageshow', this.handlePageShow, lifecycleOptions);
    this.querySelector('[data-rta-scroll-top]')?.addEventListener('click', this.handleHomeControlClick, lifecycleOptions);
    document.documentElement.addEventListener('pointerleave', (event) => this.resetOnPointerLeave(event), lifecycleOptions);
    document.documentElement.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse' && this.pointerExited && this.desktopPointer.matches) this.reveal();
    }, lifecycleOptions);
    // Keyboard and hero-focus access stay visible for this page view. External
    // controls reveal immediately without permanently locking mouse replay.
    window.addEventListener('keydown', this.revealImmediately, lifecycleOptions);
    document.addEventListener('focusin', this.handleFocusIn, lifecycleOptions);
    this.desktopPointer.addEventListener('change', () => {
      if (!this.desktopPointer.matches) this.revealImmediately();
    }, lifecycleOptions);
    this.mobileIntro.addEventListener('change', this.revealImmediately, lifecycleOptions);
    this.reducedMotion.addEventListener('change', this.handleMotionPreferenceChange, lifecycleOptions);
    this.forcedColors.addEventListener('change', this.revealImmediately, lifecycleOptions);

    if (
      window.Shopify?.designMode || (this.reducedMotion.matches && !this.mobileIntro.matches) || this.forcedColors.matches ||
      this.dataset.revealed === 'true' || !document.documentElement.classList.contains('rta-home-intro-pending')
    ) {
      this.revealImmediately();
      return;
    }

    this.armReveal();
  }

  armReveal() {
    this.revealController?.abort();
    this.revealController = new AbortController();

    const listenerOptions = { signal: this.revealController.signal };
    const passiveListenerOptions = { passive: true, signal: this.revealController.signal };

    let lastPointer = null;
    const revealOnPointerMove = (event) => {
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;

      // A stationary pointer already inside the window can receive a new
      // hover event as the page paints. Wait for real movement, not that event.
      const moved = event.movementX !== 0 || event.movementY !== 0 ||
        (lastPointer && (event.clientX !== lastPointer.x || event.clientY !== lastPointer.y));
      lastPointer = { x: event.clientX, y: event.clientY };
      if (moved) this.revealOnMovement();
    };
    window.addEventListener('pointermove', revealOnPointerMove, passiveListenerOptions);
    window.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' || event.pointerType === 'pen') this.revealOnMovement();
    }, passiveListenerOptions);
    window.addEventListener('wheel', (event) => {
      if (event.deltaY !== 0) this.revealOnMovement();
    }, passiveListenerOptions);

    // Touching the screen alone is not a scroll. Ignore restored/programmatic
    // scroll positions; reveal on an intentional vertical gesture, without
    // preventing native scrolling or link activation.
    let touchStart = null;
    window.addEventListener('touchstart', (event) => {
      touchStart = event.touches.length === 1 ? { y: event.touches[0].clientY, scroll: window.scrollY } : null;
    }, passiveListenerOptions);
    window.addEventListener('touchmove', (event) => {
      if (touchStart && event.touches.length === 1 && Math.abs(event.touches[0].clientY - touchStart.y) >= 8) this.revealOnMovement();
    }, passiveListenerOptions);
    window.addEventListener('scroll', () => {
      if (touchStart && Math.abs(window.scrollY - touchStart.scroll) >= 8) this.revealOnMovement();
    }, passiveListenerOptions);
    const clearTouch = () => { touchStart = null; };
    window.addEventListener('touchend', clearTouch, passiveListenerOptions);
    window.addEventListener('touchcancel', clearTouch, passiveListenerOptions);
    window.addEventListener('keydown', this.revealImmediately, listenerOptions);
    document.addEventListener('focusin', this.handleFocusIn, listenerOptions);
    this.reducedMotion.addEventListener('change', this.handleMotionPreferenceChange, listenerOptions);
    this.forcedColors.addEventListener('change', this.revealImmediately, listenerOptions);
    this.querySelector('.rta-home-hero__wordmark')?.addEventListener('animationend', (event) => {
      const root = document.documentElement;
      if (event.animationName === 'rta-home-wordmark-pop' && root.classList.contains('rta-home-intro-revealing')) this.finishReveal();
      if (event.animationName === 'rta-home-wordmark-return' && root.classList.contains('rta-home-intro-resetting')) this.finishReset();
    }, listenerOptions);

    // Take ownership only after every input and accessibility handler is ready.
    window.clearTimeout(window.rtaHomeIntroWatchdog);
  }

  handlePageShow(event) {
    if (!event.persisted) return;
    this.revealImmediately();
  }

  disconnectedCallback() {
    this.revealImmediately();
    this.lifecycleController?.abort();
  }

  mountSelection(candidateId = null) {
    return window.RtaHomeHeroSelection?.mount(this, candidateId);
  }

  candidateIdFromEvent(event) {
    const blockId = event.detail?.blockId || event.target?.dataset.rtaHeroCandidateBlock;
    if (!blockId) return null;

    const marker = [...this.querySelectorAll('[data-rta-hero-candidate-block]')].find(
      (candidateMarker) => candidateMarker.dataset.rtaHeroCandidateBlock === String(blockId),
    );
    return marker ? String(blockId) : null;
  }

  handleBlockSelect(event) {
    const candidateId = this.candidateIdFromEvent(event);
    if (candidateId) this.mountSelection(candidateId);
  }

  handleBlockDeselect(event) {
    if (this.candidateIdFromEvent(event)) this.mountSelection();
  }

  handleSectionLoad(event) {
    if (String(event.detail?.sectionId) === this.dataset.sectionId) this.mountSelection();
  }

  handleSectionUnload(event) {
    if (String(event.detail?.sectionId) !== this.dataset.sectionId) return;
    this.disconnectedCallback();
  }

  handleHomeControlClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  revealImmediately() {
    this.desktopReplayLocked = true;
    this.reveal(true);
  }

  handleFocusIn(event) {
    // Keep programmatic/assistive focus on hero controls safe, including the
    // account component's shadow tree. A dismissed external control must not
    // leave a permanent lock behind; its current visibility is checked on exit.
    if (event.composedPath().includes(this) || this.contains(event.target)) {
      this.revealImmediately();
    } else {
      this.reveal(true);
    }
  }

  isReplayBlockerVisible(element) {
    if (!(element instanceof Element) || !element.isConnected) return false;
    for (let node = element; node instanceof Element; node = node.parentElement || node.getRootNode().host) {
      if (node.hasAttribute('hidden') || node.hasAttribute('inert')) return false;
      const style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' ||
          Number(style.opacity) === 0) return false;
    }
    return [...element.getClientRects()].some((rect) => rect.width > 0 && rect.height > 0 &&
      rect.right > 0 && rect.bottom > 0 && rect.left < window.innerWidth && rect.top < window.innerHeight);
  }

  hasActiveExternalInterface() {
    let focused = document.activeElement;
    if (focused && focused !== document.body && focused !== document.documentElement && !this.contains(focused)) {
      while (focused.shadowRoot?.activeElement) focused = focused.shadowRoot.activeElement;
      if (this.isReplayBlockerVisible(focused)) return true;
    }
    // Modal/cart safety must also hold during delayed focus trapping or when
    // focus temporarily returns to the body. Closed/hidden interfaces do not
    // hold a permanent latch, and no banner or focus state is changed here.
    return [...document.querySelectorAll(
      'dialog[open], [role="dialog"][aria-modal="true"], cart-drawer.active, .cart-notification.active',
    )].some((element) => this.isReplayBlockerVisible(element));
  }

  handleMotionPreferenceChange() {
    // Only the mobile entrance is exempt. Desktop still settles immediately,
    // and scroll-to-top continues to read the actual motion preference.
    if (!this.mobileIntro.matches) this.revealImmediately();
  }

  revealOnMovement() {
    if (!this.mobileIntro.matches) {
      this.reveal();
      return;
    }
    if (
      this.dataset.revealed === 'true' || this.mobileRevealTimer != null ||
      !document.documentElement.classList.contains('rta-home-intro-pending')
    ) return;

    // Start once after real movement, never on load or as a touchmove debounce.
    // Keyboard/focus and lifecycle recovery can cancel this wait at any time.
    this.mobileRevealTimer = window.setTimeout(() => {
      this.mobileRevealTimer = null;
      if (!this.isConnected || !document.documentElement.classList.contains('rta-home-intro-pending')) return;
      if (window.Shopify?.designMode || this.forcedColors.matches || (this.reducedMotion.matches && !this.mobileIntro.matches)) {
        this.revealImmediately();
      } else {
        this.reveal();
      }
    }, 500);
  }

  reveal(immediate = false) {
    window.clearTimeout(this.mobileRevealTimer);
    this.mobileRevealTimer = null;
    const root = document.documentElement;
    if (immediate) {
      this.cancelReset();
      root.classList.add('rta-home-immediate-reveal');
      root.classList.remove('rta-home-intro-pending');
      this.dataset.revealed = 'true';
      this.finishReveal();
      return;
    }

    if (root.classList.contains('rta-home-intro-resetting')) {
      // Finish the fade already in progress before replaying. Rapid boundary
      // crossings replace this single intent instead of stacking timers.
      this.revealAfterReset = true;
      return;
    }

    if (this.dataset.revealed === 'true') return;
    this.pointerExited = false;

    // The fixed CSS sequence fades out at the centre, changes position only
    // while invisible, then quickly reveals the top wordmark and navigation.
    // It also gives an early pointer event a painted starting state.
    root.classList.remove('rta-home-immediate-reveal');
    root.classList.replace('rta-home-intro-pending', 'rta-home-intro-revealing');
    this.dataset.revealed = 'true';
    window.clearTimeout(window.rtaHomeIntroWatchdog);

    // animationend normally completes the 180ms sequence. Keep keyboard and
    // accessibility listeners alive until then, with a bounded recovery if an
    // animation is disabled or the end event is lost.
    this.revealTimer = window.setTimeout(() => this.finishReveal(), 280);
  }

  resetOnPointerLeave(event) {
    if (
      event.target !== document.documentElement || event.relatedTarget !== null || event.pointerType !== 'mouse' ||
      !this.desktopPointer.matches || this.desktopReplayLocked || this.reducedMotion.matches ||
      this.forcedColors.matches || window.Shopify?.designMode
    ) return;

    // The logo belongs to the hero, not a fixed overlay over later sections.
    // Do not hide the rails once browsing has moved down the landing page.
    if (this.getBoundingClientRect().top < -1 || window.scrollY > 1) return;
    if (this.hasActiveExternalInterface()) return;

    const root = document.documentElement;
    this.pointerExited = true;
    this.revealAfterReset = false;
    if (root.classList.contains('rta-home-intro-pending')) return;

    const wordmark = this.querySelector('.rta-home-hero__wordmark');
    if (!wordmark) return;
    const style = window.getComputedStyle(wordmark);
    this.style.setProperty('--rta-wordmark-return-transform', style.transform);
    this.style.setProperty('--rta-wordmark-return-opacity', style.opacity);
    // A boundary exit can interrupt either half of the wordmark fade. Keep
    // its current artwork/type until the parent is invisible, then reset.
    const artwork = wordmark.querySelector('.rta-home-hero__wordmark-art');
    const type = wordmark.querySelector('.rta-home-hero__wordmark-type');
    if (artwork && type) {
      this.style.setProperty('--rta-wordmark-return-art-opacity', window.getComputedStyle(artwork).opacity);
      this.style.setProperty('--rta-wordmark-return-type-opacity', window.getComputedStyle(type).opacity);
    }
    this.style.setProperty('--rta-shell-return-opacity', window.getComputedStyle(this.querySelector('.rta-home-shell__home')).opacity);
    window.clearTimeout(this.revealTimer);
    root.classList.remove('rta-home-intro-revealing', 'rta-home-immediate-reveal');
    root.classList.add('rta-home-intro-pending', 'rta-home-intro-resetting');
    this.dataset.revealed = 'false';
    this.armReveal();
    this.resetTimer = window.setTimeout(() => this.finishReset(), 280);
  }

  cancelReset() {
    window.clearTimeout(this.resetTimer);
    this.pointerExited = false;
    this.revealAfterReset = false;
    document.documentElement.classList.remove('rta-home-intro-resetting');
    this.style.removeProperty('--rta-wordmark-return-transform');
    this.style.removeProperty('--rta-wordmark-return-opacity');
    this.style.removeProperty('--rta-wordmark-return-art-opacity');
    this.style.removeProperty('--rta-wordmark-return-type-opacity');
    this.style.removeProperty('--rta-shell-return-opacity');
  }

  finishReset() {
    if (!document.documentElement.classList.contains('rta-home-intro-resetting')) return;
    const replay = this.revealAfterReset;
    this.cancelReset();
    this.pointerExited = true;
    if (replay) this.reveal();
  }

  finishReveal() {
    window.clearTimeout(this.revealTimer);
    window.clearTimeout(window.rtaHomeIntroWatchdog);
    document.documentElement.classList.remove('rta-home-intro-revealing');
    this.revealController?.abort();
  }
}

if (!customElements.get('rta-home-hero')) {
  customElements.define('rta-home-hero', RtaHomeHero);
}
